import React from "react";
import classNames from "classnames";
import { Typography } from "@/components/Typography";
import { BusinessHeaderVariant } from "./components/BusinessHeaderVariant";
import { VcardHeaderVariant } from "./components/VcardHeaderVariant";
import { useLogoRenderer } from "./hooks/useLogoRenderer";
import {
  HERO_HEADER_VARIANTS,
  SPACING,
  TYPOGRAPHY_CLASSES,
  type HeroHeaderVariant,
} from "./constants";
import "./PreviewHeroHeader.scss";

export interface PreviewHeroHeaderProps {
  variant: HeroHeaderVariant;
  logoUrl?: string;
  title?: string;
  subtitle?: string;
  companyName?: string;
  themeColor?: {
    backgroundColor?: string;
    buttonColor?: string;
  };
  isInitialPreview?: boolean;
  primaryAction?: {
    text?: string;
    icon?: React.ReactNode;
    onClick?: () => void;
    dataQA?: string;
  };
  logoAlt?: string;
  className?: string;
  children?: React.ReactNode;
}

/** Hero header with logo and title based on preview variant. */
export function PreviewHeroHeader({
  variant,
  logoUrl,
  title,
  subtitle,
  companyName,
  themeColor,
  isInitialPreview = false,
  primaryAction,
  logoAlt,
  className,
  children,
}: PreviewHeroHeaderProps) {
  const { renderLogo, renderAppsPlaceholder } = useLogoRenderer({
    variant,
    logoUrl,
    isInitialPreview,
    logoAlt,
    title,
  });

  const containerClasses = classNames(
    "preview-hero-header",
    `preview-hero-header--${variant}`,
    className
  );

  if (variant === HERO_HEADER_VARIANTS.BUSINESS) {
    return (
      <div className={containerClasses}>
        <BusinessHeaderVariant
          logoUrl={logoUrl}
          title={title}
          companyName={companyName}
          isInitialPreview={isInitialPreview}
          logoAlt={logoAlt}
        />
      </div>
    );
  }

  if (variant === HERO_HEADER_VARIANTS.VCARD) {
    return (
      <div className={containerClasses}>
        <VcardHeaderVariant
          logoUrl={logoUrl}
          title={title}
          subtitle={subtitle}
          themeColor={themeColor}
          isInitialPreview={isInitialPreview}
          primaryAction={primaryAction}
          logoAlt={logoAlt}
        />
      </div>
    );
  }

  if (variant === HERO_HEADER_VARIANTS.WEBSITE) {
    return (
      <div className={containerClasses}>
        {companyName && (
          <div className="preview-hero-header__company-name">
            <Typography
              Tag="p"
              classname="u-bold"
              size="s"
            >
              {companyName}
            </Typography>
          </div>
        )}
        <div className="preview-hero-header__body">
          {renderLogo()}
          <div className="preview-hero-header__divider-bottom">
            <div
              className={classNames(
                "preview-hero-header__content",
                logoUrl && "preview-hero-header__content--has-logo"
              )}
            >
              {title && (
                <div className="preview-hero-header__title">
                  <Typography
                    Tag="p"
                    size="l"
                    classname="u-bold u-spacing-4"
                  >
                    {title}
                  </Typography>
                </div>
              )}
              {subtitle && (
                <div className="preview-hero-header__subtitle">
                  <Typography
                    Tag="p"
                    size="s"
                    classname="u-spacing-5"
                  >
                    {subtitle}
                  </Typography>
                </div>
              )}
              {children}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === HERO_HEADER_VARIANTS.APPS) {
    return (
      <div className={containerClasses}>
        <div
          className="preview-hero-header__background-top"
          style={{ backgroundColor: themeColor?.buttonColor }}
        />
        <div className="preview-hero-header__background-bottom" />
        <div className="preview-hero-header__main">
          {renderLogo()}
          {renderAppsPlaceholder()}
        </div>
        <div className="preview-hero-header__content">
          {title && (
            <div className="preview-hero-header__title">
              <Typography
                Tag="p"
                size="l"
                classname={`${TYPOGRAPHY_CLASSES.SEMIBOLD} ${SPACING.MEDIUM}`}
              >
                {title}
              </Typography>
            </div>
          )}
          {subtitle && (
            <div className="preview-hero-header__subtitle">
              <Typography
                Tag="p"
                classname={SPACING.LARGE}
              >
                {subtitle}
              </Typography>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (variant === HERO_HEADER_VARIANTS.LINKS) {
    return (
      <div className={containerClasses}>
        {renderLogo()}
        {title && (
          <div className="preview-hero-header__title">
            <Typography
              Tag="p"
              size="l"
              classname={`${TYPOGRAPHY_CLASSES.SEMIBOLD} ${SPACING.MEDIUM}`}
            >
              {title}
            </Typography>
          </div>
        )}
        {subtitle && (
          <div className="preview-hero-header__subtitle">
            <Typography
              Tag="p"
              classname={SPACING.LARGE}
            >
              {subtitle}
            </Typography>
          </div>
        )}
      </div>
    );
  }

  return null;
}
